import type { FC } from "react";
import {
  Box,
  Card,
  CardContent,
  Divider,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { CrudLink, CustomCellDate } from "@/shared/components";
import { PagePaths } from "@/admin/shared/constants";
import type { AssistantPlanShowData } from "@/admin/entities/assistant";
import { statusChip } from "../model/config";

interface AssistantPlanDetailsProps {
  /** CrudShow разворачивает данные showQuery в пропсы компонента. */
  assistantPlan?: AssistantPlanShowData | null;
  id?: string;
}

const Json: FC<{ value: unknown }> = ({ value }) => (
  <Box
    component="pre"
    sx={{
      m: 0,
      p: 1,
      bgcolor: "action.hover",
      borderRadius: 1,
      fontSize: 12,
      overflowX: "auto",
    }}
  >
    {JSON.stringify(value, null, 2)}
  </Box>
);

/** План помощника партнёра целиком (п. 12 спеки): шаги с входом, результатом, ошибкой и снимками. */
export const AssistantPlanDetails: FC<AssistantPlanDetailsProps> = ({
  assistantPlan: plan,
}) => {
  if (!plan) return null;
  return (
    <Stack gap={2} sx={{ p: 2 }}>
      <Stack direction="row" gap={1} alignItems="center">
        <Typography variant="h6">{plan.title}</Typography>
        {statusChip(plan.status)}
      </Stack>
      <Stack direction="row" gap={3} flexWrap="wrap">
        {plan.organization && (
          <CrudLink
            path={PagePaths.organizations}
            tab="details"
            id={plan.organization.id}
          >
            {plan.organization.title}
          </CrudLink>
        )}
        {plan.user && (
          <CrudLink path={PagePaths.users} tab="details" id={plan.user.id}>
            {plan.user.displayName || plan.user.email}
          </CrudLink>
        )}
        <CrudLink
          path={PagePaths.assistantConversations}
          tab="details"
          id={plan.conversationId}
        >
          Диалог
        </CrudLink>
        <CustomCellDate date={plan.createdAt} />
      </Stack>
      {plan.notes.length > 0 && (
        <Typography variant="body2">
          Заметки: {plan.notes.join("; ")}
        </Typography>
      )}
      {plan.sources.map((url) => (
        <Link
          key={url}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
        >
          {url}
        </Link>
      ))}
      {plan.steps.map((step) => (
        <Card key={step.index} variant="outlined">
          <CardContent>
            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
              <Typography variant="subtitle2">
                {step.index + 1}. {step.description}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {step.operation} / {step.rootField}
              </Typography>
              {step.dangerous && (
                <Typography variant="caption" color="error">
                  опасный
                </Typography>
              )}
              {step.removedFrom.length > 0 && (
                <Typography variant="caption" color="error">
                  убирает из: {step.removedFrom.join(", ")}
                </Typography>
              )}
              <Typography variant="caption">{step.status}</Typography>
            </Stack>
            {step.error && (
              <Typography variant="body2" color="error">
                {step.error}
              </Typography>
            )}
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" color="text.secondary">
              Вход
            </Typography>
            <Json value={step.input} />
            {step.result != null && (
              <>
                <Typography variant="caption" color="text.secondary">
                  Результат
                </Typography>
                <Json value={step.result} />
              </>
            )}
            {Array.isArray(step.guards) && step.guards.length > 0 && (
              <>
                <Typography variant="caption" color="text.secondary">
                  Снимки списков
                </Typography>
                <Json value={step.guards} />
              </>
            )}
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
};
